import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { FaPlus, FaTrash, FaPen, FaMagic } from "react-icons/fa";
import SidebarPanelNavigation from "../../../../components/SidebarPanelNavigation";
import useAuth from "../../../../contexts/auth";
import {
  listWhatsAppWebTemplates,
  createWhatsAppWebTemplate,
  updateWhatsAppWebTemplate,
  deleteWhatsAppWebTemplate,
} from "../../../../services/api";

const PRODUCT_VARS = [
  { key: "nome_produto", label: "Nome do Produto", color: "primary" },
  { key: "preco_original", label: "Preço Original", color: "danger" },
  { key: "preco_desconto", label: "Preço com Desconto", color: "success" },
  { key: "percent_desconto", label: "% Desconto", color: "warning" },
  { key: "valor_economizado", label: "Valor Economizado", color: "info" },
  { key: "link_afiliado", label: "Link de Afiliado", color: "secondary" },
  { key: "cupom", label: "Cupom", color: "warning" },
];

const AI_VARS = [
  {
    key: "gatilho_cta",
    label: "Gatilho/CTA",
    defaultPrompt:
      "Crie uma frase curta e impactante destacando o principal benefício do produto, sem incluir chamada de ação, e finalize com 1 a 3 emojis coerentes com o contexto.",
  },
  {
    key: "ia",
    label: "IA Livre",
    defaultPrompt:
      "Reescreva a oferta de forma persuasiva e amigável, mantendo as informações de produto e preço.",
  },
];

export default function TemplatesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listWhatsAppWebTemplates();
      setItems(res?.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!user) return <Redirect to="/entrar" />;
  if (user.type !== "admin") return <Redirect to="/" />;

  function newTemplate() {
    setEditing({
      name: "",
      template_model: "basic",
      scope: "🔥 OFERTA IMPERDÍVEL! 🔥\nConfira {{nome_produto}} por apenas {{preco_desconto}}!\n\n🛍️ Compre agora: {{link_afiliado}}",
      coupon_scope: "",
      ai_prompts: {},
      is_default: false,
    });
  }

  function insertVar(varKey) {
    const token = `{{${varKey}}}`;
    const isAi = AI_VARS.some((v) => v.key === varKey);
    setEditing((curr) => {
      const next = { ...curr, scope: (curr.scope || "") + token };
      if (isAi && !curr.ai_prompts?.[varKey]) {
        next.ai_prompts = {
          ...curr.ai_prompts,
          [varKey]: AI_VARS.find((v) => v.key === varKey).defaultPrompt,
        };
      }
      return next;
    });
  }

  async function save() {
    if (!editing.name?.trim() || !editing.scope?.trim()) {
      alert("Nome e escopo são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await updateWhatsAppWebTemplate(editing.id, editing);
      } else {
        await createWhatsAppWebTemplate(editing);
      }
      setEditing(null);
      reload();
    } catch (err) {
      alert("Falha ao salvar template.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(t) {
    if (!window.confirm(`Excluir template "${t.name}"?`)) return;
    await deleteWhatsAppWebTemplate(t.id);
    reload();
  }

  const usedAiKeys = Object.keys(editing?.ai_prompts || {}).filter((k) =>
    (editing?.scope || "").includes(`{{${k}}}`)
  );

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3} lg={2}>
          <SidebarPanelNavigation isAdmin />
        </Col>
        <Col md={9} lg={10}>
          <div className="d-flex justify-content-between mb-3">
            <div>
              <h2 className="mb-1">📝 Templates</h2>
              <p className="text-muted">
                Modelos de mensagem com variáveis de produto + IA
              </p>
            </div>
            <Button variant="primary" onClick={newTemplate}>
              <FaPlus /> Novo Template
            </Button>
          </div>

          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          ) : items.length === 0 ? (
            <Card className="text-center p-5">
              <Card.Body>
                <p>Nenhum template criado.</p>
                <Button variant="primary" onClick={newTemplate}>
                  <FaPlus /> Criar Primeiro
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <Row>
              {items.map((t) => (
                <Col md={6} lg={4} key={t.id} className="mb-3">
                  <Card>
                    <Card.Body>
                      <div className="d-flex justify-content-between">
                        <h5>{t.name}</h5>
                        <Badge variant="info">{t.template_model}</Badge>
                      </div>
                      <pre
                        style={{
                          fontSize: 11,
                          maxHeight: 100,
                          overflow: "auto",
                          background: "rgba(255,255,255,0.05)",
                          padding: 8,
                          borderRadius: 4,
                        }}
                      >
                        {(t.scope || "").substring(0, 150)}
                      </pre>
                      <div className="d-flex" style={{ gap: 6 }}>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => setEditing(t)}
                        >
                          <FaPen /> Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(t)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Col>
      </Row>

      {/* Modal Editor */}
      <Modal
        show={!!editing}
        onHide={() => setEditing(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editing?.id ? "Editar Template" : "Novo Template"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing && (
            <>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Nome *</Form.Label>
                    <Form.Control
                      value={editing.name || ""}
                      onChange={(e) =>
                        setEditing({ ...editing, name: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Modelo</Form.Label>
                    <Form.Control
                      as="select"
                      value={editing.template_model || "basic"}
                      onChange={(e) =>
                        setEditing({ ...editing, template_model: e.target.value })
                      }
                    >
                      <option value="basic">Básico</option>
                      <option value="shopee">Shopee</option>
                      <option value="amazon">Amazon</option>
                      <option value="mercadolivre">Mercado Livre</option>
                      <option value="magalu">Magalu</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Label>Variáveis (clique pra inserir)</Form.Label>
              <div className="mb-2">
                <small className="text-muted">📦 Produto:</small>
                <div className="d-flex flex-wrap mt-1" style={{ gap: 4 }}>
                  {PRODUCT_VARS.map((v) => (
                    <Button
                      key={v.key}
                      size="sm"
                      variant={`outline-${v.color}`}
                      onClick={() => insertVar(v.key)}
                    >
                      {v.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="mb-3">
                <small className="text-muted">✨ IA:</small>
                <div className="d-flex flex-wrap mt-1" style={{ gap: 4 }}>
                  {AI_VARS.map((v) => (
                    <Button
                      key={v.key}
                      size="sm"
                      variant="outline-warning"
                      onClick={() => insertVar(v.key)}
                    >
                      <FaMagic /> {v.label}
                    </Button>
                  ))}
                </div>
              </div>

              <Form.Group>
                <Form.Label>Escopo da Mensagem *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={editing.scope || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, scope: e.target.value })
                  }
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Escopo com Cupom (opcional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editing.coupon_scope || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, coupon_scope: e.target.value })
                  }
                  placeholder="Texto alternativo quando produto tem cupom..."
                />
              </Form.Group>

              {/* Editor de prompts de IA */}
              {usedAiKeys.length > 0 && (
                <>
                  <hr />
                  <h6>✨ Comandos de IA</h6>
                  <small className="text-muted d-block mb-2">
                    Cada variável de IA gera o conteúdo via Gemini com seu
                    próprio prompt.
                  </small>
                  {usedAiKeys.map((k) => (
                    <Form.Group key={k}>
                      <Form.Label>
                        <Badge variant="warning">🔥 {k}</Badge>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={editing.ai_prompts?.[k] || ""}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            ai_prompts: {
                              ...(editing.ai_prompts || {}),
                              [k]: e.target.value,
                            },
                          })
                        }
                      />
                    </Form.Group>
                  ))}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditing(null)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
